// src/lib/reportJobs.js
// Background board pack jobs (finalize / export) that this browser asked for.
// They live in localStorage so ReportJobsWatcher can keep polling them after
// the user changes screen or reloads, and toast when the server finishes.
import { apiCall } from "@/api/apiCall";

const WATCH_KEY = "cb_watched_report_jobs";

export const JOBS_CHANGED_EVENT = "report-jobs-changed";
export const JOBS_UPDATED_EVENT = "report-jobs-updated";

export const FINISHED_STATUSES = ["done", "failed", "cancelled"];

export const JOB_STATUS_LABELS = {
  queued: "Queued",
  processing: "Generating",
  done: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
};

export const JOB_TYPE_LABELS = { finalize: "Finalize", export: "Export" };

// Entries: { id, download } — download = "pdf" | "pptx" for exports
export const getWatchedJobs = () => {
  try {
    const list = JSON.parse(localStorage.getItem(WATCH_KEY) || "[]");
    if (!Array.isArray(list)) return [];
    return list
      .map((entry) => (Number.isInteger(entry) ? { id: entry } : entry))
      .filter((entry) => Number.isInteger(entry?.id));
  } catch {
    return [];
  }
};

export const setWatchedJobs = (entries) => {
  try {
    localStorage.setItem(WATCH_KEY, JSON.stringify(entries));
  } catch {
    /* non-critical */
  }
};

export const watchReportJob = (id, download = null) => {
  const jobId = Number(id);
  if (!Number.isInteger(jobId)) return;
  const entries = getWatchedJobs().filter((entry) => entry.id !== jobId);
  setWatchedJobs([...entries, { id: jobId, download }]);
  window.dispatchEvent(new Event(JOBS_CHANGED_EVENT));
};

// Downloads in flight ("<jobId>-<type>"). Kept at module level so the state
// survives the Requests dialog being closed and reopened.
let activeDownloads = new Set();
const downloadListeners = new Set();

const setDownloadActive = (key, active) => {
  const next = new Set(activeDownloads);
  if (active) next.add(key);
  else next.delete(key);
  activeDownloads = next;
  downloadListeners.forEach((listener) => listener());
};

export const subscribeDownloads = (listener) => {
  downloadListeners.add(listener);
  return () => downloadListeners.delete(listener);
};

export const getActiveDownloads = () => activeDownloads;

export const downloadKey = (job, type) => `${job.id}-${type}`;

// Saves the file of a finished export job
export const downloadJobFile = async (job, type) => {
  const key = downloadKey(job, type);
  setDownloadActive(key, true);
  let blob;
  try {
    blob = await apiCall(
      "get",
      "/reports/jobs/download",
      { id: job.id, type },
      null,
      false,
      false,
      false,
      "blob"
    );
  } finally {
    setDownloadActive(key, false);
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${job.contract.replace(/\s+/g, "_")}_${job.month}_${job.year}.${type}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
